import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { Overlay, Button, Modal } from '@lego/klik-react';
import { EmailUnreadBold } from '@lego/klik-icons-react';
import { font, spacing } from '../../config';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_USER_CONFIRMATION_STATUS,
  SEND_INVITATION_EMAIL,
} from '../../queries/assessment';
import { generalApiErrorHandler } from '../../utils/generalApiErrorHandler';
import { useParams } from 'react-router-dom';
import {
  addSnackbar,
  snackbarDefaultMessages,
  snackbarTypes,
} from '../../utils/snackbar';
import { logger } from '../../utils/logger';
import { Spinner } from '../spinner/Spinner';
import { Link } from '@lego/klik-ui';

const modalHeaderStyle = css({
  lineHeight: 'normal',
  fontSize: font.size.size1,
  fontWeight: font.weight.medium,
});

const bodyStyle = css({
  paddingTop: spacing.size8,
});

const EmailInfoBody = ({ emails }) => {
  if (emails.length === 0) {
    return (
      <p>
        No email addresses found for this customer.
        <br />
        <br />
        Please check the customer in Contact Management.
      </p>
    );
  }

  return (
    <p>
      This will resend a reminder about this year{"'"}s assessment to{' '}
      <b>{emails.join(', ')}</b>.
      <br />
      <br />
      They can access the survey or reset their password by visiting the login
      page at{' '}
      <Link isExternal isInline href="https://survey.mcp.b2b.lego.com">
        {' '}
        survey.mcp.b2b.lego.com
      </Link>
      .
    </p>
  );
};

const EmailInfoModal = ({ customerId, onClose }) => {
  const { assessmentYear, version } = useParams();

  const {
    data: getUserConfirmationStatus,
    loading: isUserStatusLoading,
    error: getUserStatusError,
  } = useQuery(GET_USER_CONFIRMATION_STATUS, {
    variables: {
      customerId,
    },
    fetchPolicy: 'network-only',
    onError: () => {
      onClose();
    },
  });

  const confirmationStatuses =
    getUserConfirmationStatus?.getUserConfirmationStatus ?? [];

  const handleResendEmailResponse = (data) => {
    const { sendInvitationEmail } = data;

    if (!sendInvitationEmail?.wasEmailSent) {
      logger.logError('Failed to send invitation email');
      showErrorSnackbar(
        `Failed to send invitation email to contacts of ${customerId}.`
      );
      return;
    }

    addSnackbar({
      header: snackbarDefaultMessages.successHeader,
      text: sendInvitationEmail.message,
      type: snackbarTypes.SUCCESS,
    });
    onClose();
  };

  const [resendEmail, { loading: isResendEmailLoading }] = useMutation(
    SEND_INVITATION_EMAIL,
    {
      variables: {
        customerId,
        year: Number(assessmentYear),
        version: Number(version),
      },
      onCompleted: handleResendEmailResponse,
      onError: (error) => {
        generalApiErrorHandler(error, error.message);
      },
    }
  );

  const showErrorSnackbar = (text) => {
    addSnackbar({
      header: snackbarDefaultMessages.errorHeader,
      text,
      type: snackbarTypes.ERROR,
    });
  };

  async function handleResendEmail() {
    if (confirmationStatuses.length === 0) {
      showErrorSnackbar(
        `No email addresses found for customer with id ${customerId}.`
      );
      return;
    }
    try {
      await resendEmail({
        variables: {
          customerId,
          year: Number(assessmentYear),
          version: Number(version),
        },
      });
    } catch (error) {
      generalApiErrorHandler(error);
    }
  }

  if (isUserStatusLoading || isResendEmailLoading)
    return (
      <Overlay isOpen={true}>
        <Spinner />
      </Overlay>
    );

  return (
    <Overlay isOpen={true} onClick={onClose}>
      <Modal
        status={confirmationStatuses.length > 0 ? 'information' : 'error'}
        icon={<EmailUnreadBold />}
        heading={
          <Modal.Heading css={modalHeaderStyle}>
            Resend reminder email
          </Modal.Heading>
        }
        body={
          <Modal.Body>
            <div css={bodyStyle}>
              {!isUserStatusLoading && !getUserStatusError && (
                <EmailInfoBody
                  emails={confirmationStatuses.map((status) => status.email)}
                />
              )}
            </div>
          </Modal.Body>
        }
        action={
          <Modal.Action>
            <Button
              aria-label="Confirm action"
              data-transaction-name="Send Reminder"
              label="Send reminder"
              title="Send reminder email to user"
              onClick={() => {
                handleResendEmail();
              }}
              disabled={
                isUserStatusLoading ||
                isResendEmailLoading ||
                confirmationStatuses.length === 0
              }
            />
            <Button
              aria-label="Close modal"
              label="Cancel"
              variant="outline"
              onClick={onClose}
            />
          </Modal.Action>
        }
        exitClickHandler={onClose}
      />
    </Overlay>
  );
};

EmailInfoBody.propTypes = {
  emails: PropTypes.string.isRequired,
};

EmailInfoModal.propTypes = {
  customerId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export { EmailInfoModal };
